// src/client/assets/javascripts/components/SensorsTabs/Pressure/TrendOverlayTab.js
// Overlay tab of MultiDayTrendDialog: timescale bar (day/week/month/custom),
// per-sensor fetch orchestration, zoom state + history, transient waveform
// auto-loading, and the right-click axis/colors menu.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import TrendOverlayGraph from './TrendOverlayGraph';
import ChartContextMenu from './ChartContextMenu';
import {
  buildDisplayReadings,
  transientsInRange,
  getModeRange,
  toEpochMs,
  WAVEFORM_ZOOM_THRESHOLD_MS,
} from './trendResolutionUtils';
import { createZoomHistory } from './zoomHistory';
import { loadSensorColors, saveSensorColor, getSensorColor } from './trendOverlayColors';
import { getSensorLabel, filterTransientsBySeverity } from './overlayTraceUtils';
import { TRANSIENT_SEVERITY_COLORS } from './pressureTraceUtils';

const MODE_STEP_DAYS = { day: 1, week: 7, month: 30 };
const SEVERITY_LEVELS = ['low', 'medium', 'high'];
// The light-yellow "low" chip needs dark text; orange/red read fine in white.
const SEVERITY_TEXT_COLORS = { low: '#333', medium: '#fff', high: '#fff' };
const ZOOM_DEBOUNCE_MS = 150;
const DAY_MS = 24 * 60 * 60 * 1000;

const PRESSURE_UNIT_LABELS = { meter: 'Meter', bar: 'Bar', kpa: 'kPa', psi: 'PSI' };

const todayStr = () => new Date().toISOString().split('T')[0];

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
};

const inputStyle = {
  fontSize: '13px',
  padding: '4px 8px',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  height: '30px',
  boxSizing: 'border-box',
};

const TrendOverlayTab = ({
  projectId, sensorIds, pressureSensors, transientThresholds, timeZone, projectTimezone,
  pressureUnit, fetchMultiDayReading, fetchMultiDayTransients, fetchTransientReading, t,
}) => {
  const [mode, setMode] = useState('day');
  const [trendAgg, setTrendAgg] = useState('avg');
  const [anchorDate, setAnchorDate] = useState(todayStr());
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [appliedCustom, setAppliedCustom] = useState(null);
  const [sensorData, setSensorData] = useState({});
  const [failedSensorIds, setFailedSensorIds] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [visibleRange, setVisibleRange] = useState(null);
  const [axisSettings, setAxisSettings] = useState({ auto: true });
  const [severityFilter, setSeverityFilter] = useState({ low: true, medium: true, high: true });
  const [colorOverrides, setColorOverrides] = useState(() => loadSensorColors());
  const [waveforms, setWaveforms] = useState({});
  const [contextMenu, setContextMenu] = useState(null);

  const zoomHistoryRef = useRef(createZoomHistory());
  const zoomTimerRef = useRef(null);
  const waveformRequestedRef = useRef(new Set());
  const fetchGenerationRef = useRef(0);

  // Manual Y bounds are entered in the current display unit; after a global
  // unit switch they would pin the axis to a nonsense range (e.g. 2-7 Bar
  // shown as 2-7 PSI hides all data), so fall back to auto-range.
  useEffect(() => {
    setAxisSettings({ auto: true });
  }, [pressureUnit]);

  const range = useMemo(() => (
    mode === 'custom'
      ? (appliedCustom && getModeRange('custom', null, appliedCustom.from, appliedCustom.to))
      : getModeRange(mode, anchorDate)
  ), [mode, anchorDate, appliedCustom]);

  const sensorKey = sensorIds.join(',');

  useEffect(() => {
    clearTimeout(zoomTimerRef.current);
    fetchGenerationRef.current += 1;
    if (!projectId || !range || !sensorIds.length) {
      setIsFetching(false);
      return undefined;
    }
    let cancelled = false;
    setIsFetching(true);
    zoomHistoryRef.current = createZoomHistory();
    setVisibleRange(null);
    waveformRequestedRef.current = new Set();
    setWaveforms({});

    const { startDate, endDate } = range;
    // The transients endpoint treats `to` as exclusive (from == to returns an
    // empty day), so request one extra day and trim past-window events here.
    const transientsTo = addDays(endDate, 1);
    const windowEndMs = new Date(transientsTo).getTime();
    Promise.all(sensorIds.map((id) => Promise.all([
      fetchMultiDayReading(projectId, id, startDate, endDate).catch(() => null),
      fetchMultiDayTransients(projectId, id, startDate, transientsTo)
        .then((transients) => (transients || []).filter((tr) => {
          const ms = toEpochMs(tr.event_time);
          return !isNaN(ms) && ms < windowEndMs;
        }))
        .catch(() => []),
    ]).then(([reading, transients]) => [id, reading, transients])))
      .then((entries) => {
        if (cancelled) return;
        const data = {};
        const failed = [];
        entries.forEach(([id, reading, transients]) => {
          if (reading === null) failed.push(id);
          data[id] = { reading: reading || [], transients: transients || [] };
        });
        setSensorData(data);
        setFailedSensorIds(failed);
        setIsFetching(false);
      });
    return () => { cancelled = true; };
  }, [projectId, range, sensorKey]);

  // Debounced zoom pushes; null means Plotly autorange (reset).
  const handleZoomChange = useCallback((newRange) => {
    clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => {
      const zh = zoomHistoryRef.current;
      if (newRange) zh.push(newRange); else zh.reset();
      setVisibleRange(zh.current);
    }, ZOOM_DEBOUNCE_MS);
  }, []);

  useEffect(() => () => clearTimeout(zoomTimerRef.current), []);
  useEffect(() => () => { fetchGenerationRef.current += 1; }, []);

  const stepHistory = (direction) => {
    const zh = zoomHistoryRef.current;
    if (direction === 'back') zh.back();
    else if (direction === 'forward') zh.forward();
    else zh.reset();
    setVisibleRange(zh.current);
  };

  const showWaveforms = !!visibleRange
    && (visibleRange.to - visibleRange.from) <= WAVEFORM_ZOOM_THRESHOLD_MS;

  // Auto-load high-res waveforms for transients inside a deep-zoom window.
  useEffect(() => {
    // Guard the prop: a host that forgets to wire fetchTransientReading must
    // degrade to "no waveforms", not crash mid-zoom.
    if (!showWaveforms || typeof fetchTransientReading !== 'function') return;
    const generation = fetchGenerationRef.current;
    sensorIds.forEach((id) => {
      // Don't fetch waveforms for transients hidden by the severity filter.
      const visibleTransients = filterTransientsBySeverity(sensorData[id]?.transients, severityFilter, transientThresholds);
      const inWindow = transientsInRange(visibleTransients, visibleRange.from, visibleRange.to);
      inWindow.forEach((tr) => {
        if (tr.reading_id == null || waveformRequestedRef.current.has(tr.reading_id)) return;
        waveformRequestedRef.current.add(tr.reading_id);
        fetchTransientReading(projectId, id, tr.reading_id)
          .then((res) => {
            if (fetchGenerationRef.current !== generation) return;
            if (res?.status && res.data?.length) {
              setWaveforms((prev) => ({ ...prev, [tr.reading_id]: { sensorId: id, readings: res.data } }));
            }
          })
          .catch(() => {});
      });
    });
  }, [showWaveforms, visibleRange, sensorData, sensorKey, severityFilter, transientThresholds]);

  const sensorSeries = useMemo(() => sensorIds.map((id, index) => {
    const data = sensorData[id];
    const sensor = (pressureSensors || []).find((s) => String(s.ID) === String(id));
    const visibleTransients = filterTransientsBySeverity(data?.transients, severityFilter, transientThresholds);
    const visibleReadingIds = new Set(visibleTransients.map((tr) => String(tr.reading_id)));
    return {
      sensorId: id,
      label: sensor ? getSensorLabel(sensor) : String(id),
      color: getSensorColor(id, index, colorOverrides),
      displayReadings: buildDisplayReadings(data?.reading, visibleRange?.from, visibleRange?.to, trendAgg),
      transients: visibleTransients,
      waveforms: Object.entries(waveforms)
        // Hide already-loaded waveforms along with their filtered-out transient.
        .filter(([readingId, w]) => String(w.sensorId) === String(id) && visibleReadingIds.has(String(readingId)))
        .map(([readingId, w]) => ({ readingId, readings: w.readings })),
    };
  }), [sensorKey, sensorData, pressureSensors, colorOverrides, visibleRange, waveforms, trendAgg, severityFilter, transientThresholds]);

  const hasAnyData = sensorSeries.some((s) => s.displayReadings.length || s.transients.length);

  const shiftAnchor = (deltaDays) => {
    const next = new Date(new Date(anchorDate).getTime() + deltaDays * DAY_MS);
    const capped = next.getTime() > Date.now() ? new Date() : next;
    setAnchorDate(capped.toISOString().split('T')[0]);
  };

  const handleColorChange = (sensorId, color) => {
    setColorOverrides(saveSensorColor(sensorId, color));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '6px 0', flexShrink: 0 }}>
        <ButtonGroup size="medium">
          {['day', 'week', 'month', 'custom'].map((m) => (
            <Button key={m} variant={mode === m ? 'contained' : 'outlined'} onClick={() => setMode(m)}>
              {t(m)}
            </Button>
          ))}
        </ButtonGroup>
        <ButtonGroup size="medium" aria-label="trend type">
          {['avg', 'min', 'max'].map((agg) => (
            <Button key={agg} variant={trendAgg === agg ? 'contained' : 'outlined'} onClick={() => setTrendAgg(agg)}>
              {t(agg)}
            </Button>
          ))}
        </ButtonGroup>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600 }}>{t('severity')}</span>
          <ButtonGroup size="medium" aria-label="transient severity filter">
            {SEVERITY_LEVELS.map((sev) => (
              <Button
                key={sev}
                variant={severityFilter[sev] ? 'contained' : 'outlined'}
                onClick={() => setSeverityFilter((prev) => ({ ...prev, [sev]: !prev[sev] }))}
                sx={severityFilter[sev] ? {
                  backgroundColor: TRANSIENT_SEVERITY_COLORS[sev],
                  color: SEVERITY_TEXT_COLORS[sev],
                  '&:hover': { backgroundColor: TRANSIENT_SEVERITY_COLORS[sev], filter: 'brightness(0.92)' },
                } : { color: '#888', borderColor: '#ccc' }}
              >
                {t(sev)}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        {mode !== 'custom' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconButton
              size="small"
              onClick={() => shiftAnchor(-MODE_STEP_DAYS[mode])}
              aria-label="previous period"
              sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', width: 30, height: 30 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <input
              type="date"
              value={anchorDate}
              max={todayStr()}
              onChange={(e) => e.target.value && setAnchorDate(e.target.value)}
              style={inputStyle}
            />
            <IconButton
              size="small"
              disabled={anchorDate >= todayStr()}
              onClick={() => shiftAnchor(MODE_STEP_DAYS[mode])}
              aria-label="next period"
              sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', width: 30, height: 30 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={inputStyle} />
            <span>—</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={inputStyle} />
            <Button
              size="small"
              variant="contained"
              disabled={!customFrom || !customTo || customTo < customFrom}
              onClick={() => setAppliedCustom({ from: customFrom, to: customTo })}
            >
              {t('load')}
            </Button>
          </div>
        )}
        {isFetching && <span style={{ color: '#666', fontSize: '12px' }}>{t('loading')}</span>}
        {failedSensorIds.length > 0 && (
          <span style={{ color: 'red', fontSize: '12px' }}>
            {`${t('no_data')}: ${failedSensorIds.join(', ')}`}
          </span>
        )}
      </div>

      {hasAnyData ? (
        <TrendOverlayGraph
          sensorSeries={sensorSeries}
          timeZone={timeZone}
          projectTimezone={projectTimezone}
          transientThresholds={transientThresholds}
          showWaveforms={showWaveforms}
          visibleRange={visibleRange}
          axisSettings={axisSettings}
          onZoomChange={handleZoomChange}
          onOpenContextMenu={setContextMenu}
          canBack={zoomHistoryRef.current.canBack()}
          canForward={zoomHistoryRef.current.canForward()}
          onBack={() => stepHistory('back')}
          onForward={() => stepHistory('forward')}
          onReset={() => stepHistory('reset')}
          t={t}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
          {isFetching ? t('loading') : t('no_data')}
        </div>
      )}

      <ChartContextMenu
        open={!!contextMenu}
        position={contextMenu}
        onClose={() => setContextMenu(null)}
        axisSettings={axisSettings}
        onApplyAxis={setAxisSettings}
        sensors={sensorSeries.map(({ sensorId, label, color }) => ({ sensorId, label, color }))}
        onColorChange={handleColorChange}
        unitLabel={PRESSURE_UNIT_LABELS[pressureUnit] || 'Meter'}
        t={t}
      />
    </div>
  );
};

TrendOverlayTab.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sensorIds: PropTypes.array.isRequired,
  pressureSensors: PropTypes.array,
  transientThresholds: PropTypes.object,
  timeZone: PropTypes.string,
  projectTimezone: PropTypes.string,
  pressureUnit: PropTypes.string,
  fetchMultiDayReading: PropTypes.func.isRequired,
  fetchMultiDayTransients: PropTypes.func.isRequired,
  fetchTransientReading: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default TrendOverlayTab;
